import "@/App.css";
import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Check, 
  X, 
  ChevronDown,
  ChevronUp,
  Zap,
  Quote,
  Brain,
  Heart,
  Battery,
  Moon,
  Eye,
  User,
  Repeat,
  MessageCircle,
  Users,
  FileText,
  Headphones,
  Shield,
  Mail
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Track analytics event
const trackEvent = async (eventType, eventData = null) => {
  try {
    await axios.post(`${API}/analytics/event`, {
      event_type: eventType,
      event_data: eventData
    });
  } catch (e) {
    console.log("Analytics error:", e);
  }
};

// Images
const IMAGES = {
  groupPhoto: "https://customer-assets.emergentagent.com/job_mindful-reset-9/artifacts/gz7kp62e_522231fa-e073-4228-ae53-595b15f209df.JPG",
  breathworkSession: "https://customer-assets.emergentagent.com/job_mindful-reset-9/artifacts/dgaxwhva_c4d9c74d-be4e-4c09-9f4b-674f20fab709.JPG"
};

// Constants
const STRIPE_LINK = "https://buy.stripe.com/6oU9AT3NAfM19ZQg0s2cg0A";
const CONTACT = {
  email: "marcus@marcusjonescoaching.co.uk"
};

// Hero Section
const HeroSection = () => (
  <section className="min-h-screen relative flex items-center" data-testid="hero-section">
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${IMAGES.breathworkSession})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70" />
    
    <div className="relative z-10 px-6 md:px-12 max-w-7xl mx-auto w-full py-24">
      <div className="max-w-2xl">
        <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-6 block">
          Only 10 Places Available
        </span>
        
        <h1 className="font-['Syne'] font-extrabold text-5xl sm:text-6xl md:text-7xl tracking-tighter uppercase leading-[0.9] text-white mb-4">
          Breathe To
          <br />
          <span className="text-[#7dfabf]">Breakthrough</span>
        </h1>
        
        <p className="font-['Syne'] font-bold text-xl md:text-2xl text-white mt-6 uppercase tracking-wide">
          A 6-Week Conscious Connected Breathwork Program
        </p>
        
        <p className="font-['Inter'] text-lg text-zinc-300 mt-6 leading-relaxed">
          For anyone tired of carrying everything internally, tired of keeping it all together on the surface while feeling overwhelmed underneath.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 mt-10">
          <a 
            href={STRIPE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('cta_click', { location: 'hero', button: 'join_program' })}
            className="btn-primary bg-[#7dfabf] text-black font-bold uppercase tracking-wide px-8 py-4 rounded-none inline-flex items-center justify-center gap-2"
            data-testid="hero-cta-button"
          >
            <Zap className="w-5 h-5" />
            <span>Join The Program – £297</span>
          </a>
          
          <button 
            onClick={() => {
              const element = document.getElementById('why-this-matters');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-transparent border border-zinc-700 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-none hover:border-[#7dfabf] hover:text-[#7dfabf] transition-all duration-300 inline-flex items-center justify-center gap-2"
            data-testid="hero-learn-more-button"
          >
            Learn More
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  </section>
);

// Why This Matters Section
const WhyThisMattersSection = () => (
  <section id="why-this-matters" className="py-24 md:py-32 px-6 md:px-12 bg-black" data-testid="why-matters-section">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-16">
        <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
          The Truth
        </span>
        <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white">
          Why This Work Matters
        </h2>
      </div>
      
      <div className="space-y-6 font-['Inter'] text-lg text-zinc-300 leading-relaxed">
        <p>
          Your nervous system is the <span className="text-white font-semibold">operating system for your entire life.</span>
        </p>
        <p>
          When it's overloaded, everything becomes more difficult.
        </p>
        
        <div className="bg-[#111111] border border-zinc-800 p-8 my-8">
          <p className="text-zinc-400 mb-4">The cycle most people are stuck in:</p>
          <ul className="space-y-2 text-zinc-300">
            <li>→ You wake up stressed.</li>
            <li>→ You go through the day on autopilot.</li>
            <li>→ You crash at night.</li>
            <li>→ You distract yourself for temporary relief.</li>
            <li>→ And the cycle repeats.</li>
          </ul>
        </div>
        
        <p className="text-white text-xl font-['Syne'] font-bold">
          This impacts everything:
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 my-8">
          {['Mood', 'Sleep', 'Motivation', 'Focus', 'Energy', 'Emotional Resilience', 'Relationships', 'Confidence', 'Sense of Self'].map((item, i) => (
            <div key={i} className="bg-[#0a0a0a] border border-zinc-800 px-4 py-3 text-center">
              <span className="text-[#7dfabf] font-medium">{item}</span>
            </div>
          ))}
        </div>
        
        <div className="border-l-4 border-[#7dfabf] pl-6 my-8">
          <p className="text-white text-xl">
            You don't need to "try harder."<br />
            You don't need more discipline.<br />
            <span className="text-[#7dfabf]">You need to work with your body, not against it.</span>
          </p>
        </div>
        
        <p className="text-center text-white text-xl font-['Syne'] font-bold">
          Breathwork does that. It creates change from the inside out.
        </p>
      </div>
    </div>
  </section>
);

// Struggles Section
const StrugglesSection = () => {
  const struggles = [
    "Constant overthinking",
    "Feeling unsettled or overwhelmed",
    "Difficulty relaxing even when you have time",
    "Numbness or emotional shutdown",
    "Stress building up in your chest, jaw, or stomach",
    "Trouble sleeping or switching off",
    "Feeling disconnected from yourself or others",
    "Low energy, low motivation, low spark",
    "A loop of habits you keep falling back into",
    "Relying on distractions, food, alcohol, or work to escape",
    "Carrying things internally with no outlet",
    "Feeling stuck, lost, or just not like yourself"
  ];
  
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]" data-testid="struggles-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
            Sound Familiar?
          </span>
          <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white">
            What You May Be Struggling With
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {struggles.map((struggle, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 bg-[#111111] border border-zinc-800 p-4 card-hover"
            >
              <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <span className="font-['Inter'] text-zinc-300">{struggle}</span>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="font-['Inter'] text-lg text-zinc-400 mb-4">
            None of this means you're broken.
          </p>
          <p className="font-['Syne'] font-bold text-xl text-white">
            It means you've gone too long without release, regulation, or space to feel.
          </p>
          <p className="font-['Inter'] text-[#7dfabf] text-lg mt-4">
            Breathwork changes that.
          </p>
        </div>
      </div>
    </section>
  );
};

// What You'll Get Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Heart,
      title: "Emotional Release",
      description: "Learn how to let go of the stress, tension, and stored emotion your body has been carrying without having to relive or retell stories."
    },
    {
      icon: Battery,
      title: "Nervous System Reset",
      description: "Move from wired and reactive to grounded and calm. Your body will stop living in the red zone."
    },
    {
      icon: Brain,
      title: "Mental Clarity",
      description: "Your mind clears. Decision-making gets easier. Your thoughts stop spiralling."
    },
    {
      icon: Zap,
      title: "More Energy + Presence",
      description: "When your system stops fighting itself, energy comes back. You feel more alive, more present, more 'here.'"
    },
    {
      icon: Moon,
      title: "Better Sleep",
      description: "Your system finally receives the signal to shut down rather than collapse from exhaustion."
    },
    {
      icon: Eye,
      title: "Awareness of Your Patterns",
      description: "Understand why you react the way you do and how to change it. This is where real growth happens."
    },
    {
      icon: User,
      title: "Stronger Connection to Yourself",
      description: "Meet the version of you underneath the stress, coping, and noise."
    },
    {
      icon: Repeat,
      title: "A Repeatable Breathwork Practice",
      description: "A lifelong tool you can use for clarity, grounding, emotional release, or energy whenever you need it."
    }
  ];
  
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-black" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
            The Transformation
          </span>
          <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white">
            What You'll Get From This Program
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-[#111111] border border-zinc-800 p-6 card-hover"
              data-testid={`benefit-${index}`}
            >
              <div className="w-12 h-12 bg-[#7dfabf] flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-black" />
              </div>
              <h3 className="font-['Syne'] font-bold text-lg text-white mb-3 uppercase">
                {benefit.title}
              </h3>
              <p className="font-['Inter'] text-sm text-zinc-400 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// What's Included Section
const IncludedSection = () => {
  const included = [
    {
      icon: Headphones,
      title: "Weekly Guided Breathwork Sessions",
      description: "Designed around a specific theme to help you release, regulate, and move forward."
    },
    {
      icon: FileText,
      title: "Reflection PDFs + Inner Work Exercises",
      description: "Built from the kind of work that helps you understand your patterns, triggers, expectations, relationships, and your internal world."
    },
    {
      icon: Repeat,
      title: "Integration Guidance",
      description: "So the emotional and physical shifts turn into real-life changes."
    },
    {
      icon: Users,
      title: "Private Community Space",
      description: "Share insights, wins, challenges, or just watch and absorb. No pressure."
    },
    {
      icon: MessageCircle,
      title: "Direct Access to Marcus",
      description: "Support. Direction. Accountability throughout the six weeks."
    },
    {
      icon: Shield,
      title: "Safety + Foundational Guidance",
      description: "Learn how to breathe safely, stay regulated, and honour your system."
    }
  ];
  
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]" data-testid="included-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
            Inside The Program
          </span>
          <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white">
            What's Included
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {included.map((item, index) => (
            <div 
              key={index}
              className="flex gap-4 bg-[#111111] border border-zinc-800 p-6 card-hover"
            >
              <div className="w-12 h-12 bg-[#7dfabf] flex items-center justify-center flex-shrink-0">
                <item.icon className="w-6 h-6 text-black" />
              </div>
              <div>
                <h3 className="font-['Syne'] font-bold text-lg text-white mb-2">
                  {item.title}
                </h3>
                <p className="font-['Inter'] text-sm text-zinc-400 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Image Section
const ImageSection = () => (
  <section className="py-16 px-6 md:px-12 bg-black" data-testid="image-section">
    <div className="max-w-5xl mx-auto">
      <div className="overflow-hidden">
        <img 
          src={IMAGES.groupPhoto} 
          alt="Breathwork group session" 
          className="w-full h-auto object-cover img-hover"
        />
      </div>
    </div>
  </section>
);

// Who This Is For Section
const AudienceSection = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]" data-testid="audience-section">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-16">
        <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
          Be Honest With Yourself
        </span>
        <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white">
          Who This Is For
        </h2>
      </div>
      
      <div className="grid md:grid-cols-2 gap-8">
        {/* For You */}
        <div className="bg-[#111111] border border-[#7dfabf] p-8" data-testid="for-you-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#7dfabf] flex items-center justify-center">
              <Check className="w-6 h-6 text-black" />
            </div>
            <h3 className="font-['Syne'] font-bold text-xl text-[#7dfabf] uppercase">
              This Is For You If...
            </h3>
          </div>
          
          <ul className="space-y-3">
            {[
              "You want to feel more grounded and present",
              "You're open to breathing deeper than surface-level calm",
              "You feel stuck, overwhelmed, or disconnected",
              "You want to break old emotional or behavioural patterns",
              "You want to improve sleep, energy, clarity, and internal peace",
              "You're willing to show up each week and engage honestly",
              "You're ready for a powerful shift internally and externally"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#7dfabf] mt-0.5 flex-shrink-0" />
                <span className="font-['Inter'] text-zinc-300 text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Not For You */}
        <div className="bg-[#0a0a0a] border border-zinc-800 p-8" data-testid="not-for-you-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-zinc-800 flex items-center justify-center">
              <X className="w-6 h-6 text-zinc-500" />
            </div>
            <h3 className="font-['Syne'] font-bold text-xl text-zinc-500 uppercase">
              This Is Not For You If...
            </h3>
          </div>
          
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-zinc-600 mt-0.5 flex-shrink-0" />
              <span className="font-['Inter'] text-zinc-500 text-sm">You're looking for a quick fix</span>
            </li>
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-zinc-600 mt-0.5 flex-shrink-0" />
              <span className="font-['Inter'] text-zinc-500 text-sm">You're not willing to do the work</span>
            </li>
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-zinc-600 mt-0.5 flex-shrink-0" />
              <span className="font-['Inter'] text-zinc-500 text-sm">You want someone else to fix you</span>
            </li>
          </ul>
          
          <p className="font-['Inter'] text-zinc-500 text-sm mt-6 border-t border-zinc-800 pt-6">
            This is real work. It requires commitment and honesty with yourself.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Why Only 10 Section
const WhyTenSection = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-black" data-testid="why-ten-section">
    <div className="max-w-3xl mx-auto text-center">
      <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
        Limited Spaces
      </span>
      <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white mb-8">
        Why Only 10 People?
      </h2>
      
      <div className="space-y-6 font-['Inter'] text-lg text-zinc-300 leading-relaxed text-left">
        <p>
          I'm keeping the group small so I can actually <span className="text-white font-semibold">see you, support you, and help you integrate what comes up.</span>
        </p>
        
        <p className="text-[#7dfabf]">
          Because transformation doesn't happen in a noisy room.
        </p>
        
        <p>
          A small group creates trust, consistency, and momentum.
        </p>
        
        <div className="bg-[#111111] border border-[#7dfabf] p-8 my-8">
          <p className="font-['Syne'] font-bold text-xl text-white text-center">
            In return, you get the deepest level of support I'll ever offer in this program.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// The Commitment Section
const CommitmentSection = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]" data-testid="commitment-section">
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-12">
        <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
          What's Required
        </span>
        <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white">
          The Commitment
        </h2>
      </div>
      
      <div className="bg-[#111111] border border-zinc-800 p-8">
        <p className="font-['Inter'] text-zinc-300 mb-6">Over the six weeks, you'll:</p>
        
        <ul className="space-y-4">
          {[
            "Complete one guided session per week",
            "Work through the reflection PDFs",
            "Follow the integration steps",
            "Stay open, honest, and connected to the process"
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#7dfabf] mt-0.5 flex-shrink-0" />
              <span className="font-['Inter'] text-white">{item}</span>
            </li>
          ))}
        </ul>
        
        <div className="border-t border-zinc-800 mt-8 pt-8">
          <p className="font-['Inter'] text-zinc-400 text-center">
            That's all you need. You don't have to "perform." You don't have to know how to feel.
          </p>
          <p className="font-['Syne'] font-bold text-xl text-[#7dfabf] text-center mt-4">
            You just have to show up. Your breath will do the rest.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "I've never experienced anything like it. You've found your calling."
    },
    {
      quote: "It took me a while to get into it, but once I did… I went into deep relaxation. My whole body was tingling. I felt like I was floating."
    },
    {
      quote: "I'm not someone who usually opens up, but something shifted. That had been sitting there for years."
    },
    {
      quote: "I came in stressed and anxious. I left feeling clear, calm, and reset."
    },
    {
      quote: "I didn't expect much if I'm honest… but that was powerful."
    },
    {
      quote: "You made it feel safe from the start. That's what allowed me to actually go there."
    }
  ];
  
  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]" data-testid="testimonials-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
            Real Results
          </span>
          <h2 className="font-['Syne'] font-bold text-4xl md:text-5xl uppercase text-white">
            What People Say
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-[#111111] border border-zinc-800 p-8 relative card-hover"
              data-testid={`testimonial-${index}`}
            >
              <Quote className="w-10 h-10 text-[#7dfabf] opacity-30 absolute top-6 right-6" />
              <p className="font-['Inter'] text-lg text-zinc-300 leading-relaxed relative z-10 italic">
                "{testimonial.quote}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-black border-t border-zinc-800" data-testid="cta-section">
    <div className="max-w-3xl mx-auto text-center">
      <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
        Your Next Step
      </span>
      <h2 className="font-['Syne'] font-bold text-4xl md:text-6xl uppercase text-white mb-6">
        Ready To Breakthrough?
      </h2>
      
      <p className="font-['Inter'] text-lg text-zinc-400 mb-4">
        If you want one of the 10 places, message me:
      </p>
      
      <p className="font-['Syne'] font-bold text-3xl text-[#7dfabf] mb-8">
        "BREATHWORK10"
      </p>
      
      <p className="font-['Inter'] text-zinc-400 mb-10">
        Or sign up directly below:
      </p>
      
      <a 
        href={STRIPE_LINK}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent('cta_click', { location: 'bottom_cta', button: 'join_program' })}
        className="btn-primary bg-[#7dfabf] text-black font-bold uppercase tracking-wide px-12 py-5 rounded-none inline-flex items-center justify-center gap-3 text-lg"
        data-testid="cta-book-button"
      >
        <Zap className="w-6 h-6" />
        <span>Join The Program – £297</span>
      </a>
      
      <p className="font-['Inter'] text-sm text-zinc-600 mt-6">
        Once the group is full, it's closed.
      </p>
      
      <div className="mt-12 border-t border-zinc-800 pt-12">
        <p className="font-['Inter'] text-lg text-zinc-300 italic">
          "If you feel something pull you towards this, don't ignore it.<br />
          <span className="text-[#7dfabf]">Your body knows when it's time for change.</span>"
        </p>
      </div>
    </div>
  </section>
);

// Community Section
const CommunitySection = () => {
  return (
    <section className="py-16 px-6 md:px-12 bg-[#111111] border-y border-zinc-800" data-testid="community-section">
      <div className="max-w-2xl mx-auto text-center">
        <span className="font-['Inter'] text-xs font-bold tracking-[0.3em] uppercase text-[#7dfabf] mb-4 block">
          Not Ready Yet?
        </span>
        <h3 className="font-['Syne'] font-bold text-2xl md:text-3xl text-white uppercase mb-4">
          Join The Community
        </h3>
        <p className="font-['Inter'] text-zinc-400 mb-8">
          Connect with Marcus and find out more about breathwork, mindset, and wellness.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://www.skool.com/booze-to-beast"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('community_click', { platform: 'skool' })}
            className="btn-primary bg-[#7dfabf] text-black font-bold uppercase tracking-wide px-8 py-4 rounded-none inline-flex items-center justify-center gap-3 hover:bg-white transition-all duration-300"
            data-testid="skool-link"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Join Free Skool Group
          </a>
          
          <a
            href="https://www.instagram.com/marcusjonescoaching/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('community_click', { platform: 'instagram' })}
            className="bg-transparent border border-[#7dfabf] text-[#7dfabf] font-bold uppercase tracking-wide px-8 py-4 rounded-none hover:bg-[#7dfabf] hover:text-black transition-all duration-300 inline-flex items-center justify-center gap-3"
            data-testid="instagram-link"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            Follow on Instagram
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => (
  <section className="py-16 px-6 md:px-12 bg-black border-t border-zinc-800" data-testid="contact-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-10">
        <h3 className="font-['Syne'] font-bold text-2xl text-white uppercase mb-2">
          Questions?
        </h3>
        <p className="font-['Inter'] text-zinc-400">
          Get in touch with Marcus directly
        </p>
      </div>
      
      <div className="flex justify-center items-center">
        <a 
          href={`mailto:${CONTACT.email}?subject=BREATHWORK10`}
          className="flex items-center gap-3 text-zinc-300 hover:text-[#7dfabf] transition-colors duration-300"
          data-testid="contact-email"
        >
          <Mail className="w-5 h-5 text-[#7dfabf]" />
          <span className="font-['Inter']">{CONTACT.email}</span>
        </a>
      </div>
      
      <p className="font-['Inter'] text-xs text-zinc-600 text-center mt-4">
        Use subject line: <span className="text-[#7dfabf]">BREATHWORK10</span>
      </p>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-8 px-6 md:px-12 bg-[#050505] border-t border-zinc-900" data-testid="footer">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
      <div className="font-['Syne'] font-bold text-lg text-white uppercase">
        Breathe To Breakthrough
      </div>
      
      <p className="font-['Inter'] text-xs text-zinc-600">
        © 2026 Marcus Jones Coaching. All rights reserved.
      </p>
    </div>
  </footer>
);

// Main App Component
function App() {
  useEffect(() => {
    trackEvent('page_view', { page: 'breathe_to_breakthrough' });
  }, []);
  
  return (
    <div className="min-h-screen bg-black">
      <HeroSection />
      <WhyThisMattersSection />
      <StrugglesSection />
      <BenefitsSection />
      <IncludedSection />
      <ImageSection />
      <TestimonialsSection />
      <AudienceSection />
      <WhyTenSection />
      <CommitmentSection />
      <CTASection />
      <CommunitySection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
